import { format } from "date-fns";
import type { FormEvent } from "react";
import type { CreateIncidentLogMessageSchema } from "features/CenterContent/RoleContent/ISirk/types";
import { useISirklogMessagePublish } from "features/CenterContent/RoleContent/ISirk/useISirkData";
import { useISirkLogMessages } from "features/CenterContent/RoleContent/ISirk/useISirkLogMessages";
import { SpeechBubbleOutline24Icon } from "@vygruppen/spor-icon-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { ActionCardButton } from "shared/components/drops/ActionCardButton";
import { ActionCard } from "shared/components/drops/ActionCard";
import { LogEntry } from "features/CenterContent/VehicleDetails/shared/LogEntry";
import { compareDateStringAsc } from "shared/utils/dateTimeUtils";
// eslint-disable-next-line drops/no-direct-spor-import
import { Field, Stack, Text, Textarea } from "@vygruppen/spor-react";

type ISirkLogModuleProps = { isirk: string };

const ISirkLogModule = ({ isirk }: ISirkLogModuleProps) => {
  const { t } = useTranslation();
  const formId = useId();
  const [message, setMessage] = useState("");
  const { isError, isSuccess, data } = useISirkLogMessages(isirk);
  const { postIncidentLog, mutationStatus } = useISirklogMessagePublish();

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedMessage = message.trim();
    if (!trimmedMessage || mutationStatus === "pending") return;

    const payload: CreateIncidentLogMessageSchema = {
      message: trimmedMessage,
      isirkId: isirk,
      incidentId: null,
      date: format(Date.now(), "yyyy-MM-dd"),
    };
    postIncidentLog(payload, {
      onSuccess: () =>
        setMessage((currentMessage) =>
          currentMessage === message ? "" : currentMessage,
        ),
    });
  };

  return (
    <Stack gap="2" align="stretch">
      <ActionCard
        title={t("Ny melding")}
        icon={<SpeechBubbleOutline24Icon aria-hidden="true" />}
        primaryAction={
          <ActionCardButton
            form={formId}
            type="submit"
            variant="primary"
            loading={mutationStatus === "pending"}
            disabled={!message.trim() || mutationStatus === "pending"}
          >
            {t("Send til I-sirk-logg")}
          </ActionCardButton>
        }
      >
        <form id={formId} onSubmit={onSubmit}>
          <Field w="100%">
            <Textarea
              label={t("Skriv loggmelding")}
              name="logmessage"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              minHeight="7rem"
            />
          </Field>
        </form>
      </ActionCard>

      {mutationStatus === "error" && (
        <FailureMessage
          customMessage={t(
            "Kunne ikke sende loggmelding. Prøv igjen, eller kontakt IT hvis feilen vedvarer.",
          )}
        />
      )}
      {isError && (
        <FailureMessage customMessage={t("Feil med henting av loggmelding")} />
      )}

      {isSuccess &&
        (data?.incidentLogs.length ? (
          <Stack gap="1" align="stretch">
            {[...data.incidentLogs]
              .sort((a, b) => compareDateStringAsc(a.timestamp, b.timestamp))
              .map((log, index, logs) => (
                <LogEntry
                  key={log.id}
                  timestamp={log.timestamp}
                  message={log.message}
                  source={log.sourceSystem.author?.split("@")[0]}
                  fallbackSource={log.sourceSystem.name}
                  hideDashedLine={index === logs.length - 1}
                />
              ))}
          </Stack>
        ) : (
          <Text variant="sm">{t("Ingen meldinger i loggen")}</Text>
        ))}
    </Stack>
  );
};

export default ISirkLogModule;
